import { useState, useEffect, useRef, useMemo } from "react";
import {
  Play,
  X,
  ChevronLeft,
  ChevronRight,
  Image as ImageIcon,
  Video,
} from "lucide-react";

// 25 éléments (18 photos + 7 vidéos) mélangés pour créer un flux visuel varié
const rawItems = [
  { type: "video", src: "/assets/realisations/video-1.mp4" },
  { type: "image", src: "/assets/realisations/img-1.jpg" },
  { type: "image", src: "/assets/realisations/img-2.jpg" },
  { type: "video", src: "/assets/realisations/video-2.mp4" },
  { type: "image", src: "/assets/realisations/img-3.jpg" },
  { type: "image", src: "/assets/realisations/img-4.jpg" },
  { type: "image", src: "/assets/realisations/img-5.jpg" },
  { type: "video", src: "/assets/realisations/video-3.mp4" },
  { type: "image", src: "/assets/realisations/img-6.jpg" },
  { type: "image", src: "/assets/realisations/img-7.jpg" },
  { type: "video", src: "/assets/realisations/video-4.mp4" },
  { type: "image", src: "/assets/realisations/img-8.jpg" },
  { type: "image", src: "/assets/realisations/img-9.jpg" },
  { type: "image", src: "/assets/realisations/img-10.jpg" },
  { type: "video", src: "/assets/realisations/video-5.mp4" },
  { type: "image", src: "/assets/realisations/img-11.jpg" },
  { type: "image", src: "/assets/realisations/img-12.jpg" },
  { type: "image", src: "/assets/realisations/img-13.jpg" },
  { type: "image", src: "/assets/realisations/img-14.jpg" },
  { type: "video", src: "/assets/realisations/video-6.mp4" },
  { type: "image", src: "/assets/realisations/img-15.jpg" },
  { type: "image", src: "/assets/realisations/img-16.jpg" },
  { type: "image", src: "/assets/realisations/img-17.jpg" },
  { type: "image", src: "/assets/realisations/img-18.jpg" },
  { type: "video", src: "/assets/realisations/video-7.mp4" },
];

// Pattern bento : applique des spans réguliers pour créer du rythme
const bentoPattern = [
  "big", "", "", "", "tall", "",
  "", "big", "", "", "", "tall",
  "big", "", "", "", "tall", "",
  "", "big", "", "tall", "", "", "",
];

const items = rawItems.map((it, i) => ({
  ...it,
  span: bentoPattern[i] || "",
  title:
    it.type === "video"
      ? `Intervention · Réalisation n°${i + 1}`
      : `Réparation DSP · n°${i + 1}`,
}));

const filters = [
  { id: "all", label: "Tout", icon: null, count: items.length },
  {
    id: "video",
    label: "Vidéos",
    icon: Video,
    count: items.filter((i) => i.type === "video").length,
  },
  {
    id: "image",
    label: "Photos",
    icon: ImageIcon,
    count: items.filter((i) => i.type === "image").length,
  },
];

// Vidéo lazy : ne charge le flux qu'une fois visible
const LazyVideo = ({ src }) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisible(true);
            io.disconnect();
          }
        });
      },
      { rootMargin: "200px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <div ref={ref} className="w-full h-full bg-[#050505]">
      {visible && (
        <video
          src={src}
          muted
          loop
          playsInline
          autoPlay
          preload="metadata"
          className="w-full h-full object-cover"
        />
      )}
    </div>
  );
};

export const Realisations = () => {
  const [filter, setFilter] = useState("all");
  const [lightbox, setLightbox] = useState(null);
  const [visibleCount, setVisibleCount] = useState(12);

  const filtered = useMemo(
    () =>
      items
        .map((it, i) => ({ ...it, originalIndex: i }))
        .filter((it) => filter === "all" || it.type === filter),
    [filter]
  );

  const displayed = filtered.slice(0, visibleCount);
  const hasMore = filtered.length > visibleCount;

  // Reset pagination & close lightbox when filter changes
  useEffect(() => {
    setVisibleCount(12);
    setLightbox(null);
  }, [filter]);

  const openLightbox = (idx) => setLightbox(idx);
  const closeLightbox = () => setLightbox(null);

  const next = () => {
    if (lightbox === null) return;
    setLightbox((lightbox + 1) % filtered.length);
  };
  const prev = () => {
    if (lightbox === null) return;
    setLightbox((lightbox - 1 + filtered.length) % filtered.length);
  };

  useEffect(() => {
    const onKey = (e) => {
      if (lightbox === null) return;
      if (e.key === "Escape") closeLightbox();
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = lightbox !== null ? "hidden" : "";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lightbox]);

  return (
    <section
      id="realisations"
      data-testid="realisations-section"
      className="relative bg-[#0A0A0A] py-24 md:py-32 overflow-hidden border-t border-white/10"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="grid lg:grid-cols-12 gap-10 items-end mb-14">
          <div className="lg:col-span-7">
            <div className="text-xs font-bold uppercase tracking-[0.3em] text-[#007AFF] mb-4">
              — Nos réalisations
            </div>
            <h2
              data-testid="realisations-title"
              className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black uppercase tracking-tighter leading-[0.95]"
            >
              Le travail<br />
              parle de <span className="text-[#EF4135]">lui-même</span>
            </h2>
          </div>
          <div className="lg:col-span-5 flex flex-col gap-6">
            <p className="text-base text-white/60 leading-relaxed">
              Découvrez {items.length} réalisations : interventions sur grêle,
              chocs de parking, petites bosses. Chaque chantier est une
              démonstration de précision artisanale.
            </p>

            {/* Filters */}
            <div
              data-testid="realisations-filters"
              className="flex flex-wrap gap-2"
            >
              {filters.map((f) => {
                const active = filter === f.id;
                return (
                  <button
                    key={f.id}
                    onClick={() => setFilter(f.id)}
                    data-testid={`filter-${f.id}`}
                    className={`inline-flex items-center gap-2 px-4 py-2 text-xs font-bold uppercase tracking-widest border transition-all ${
                      active
                        ? "bg-[#007AFF] text-white border-[#007AFF]"
                        : "bg-transparent text-white/60 border-white/15 hover:border-white/40 hover:text-white"
                    }`}
                  >
                    {f.icon && <f.icon className="w-3.5 h-3.5" />}
                    {f.label}
                    <span
                      className={`text-[10px] ${
                        active ? "text-white/80" : "text-white/40"
                      }`}
                    >
                      ({f.count})
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* French flag divider */}
        <div className="flex items-center gap-1 mb-10">
          <div className="h-[2px] w-16 bg-[#0055A4]" />
          <div className="h-[2px] w-16 bg-white" />
          <div className="h-[2px] w-16 bg-[#EF4135]" />
          <div className="h-[2px] flex-1 bg-white/10" />
        </div>

        {/* Bento grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 auto-rows-[200px] md:auto-rows-[220px] gap-4 md:gap-5">
          {displayed.map((item, idx) => {
            const spanClasses =
              item.span === "big"
                ? "col-span-2 row-span-2"
                : item.span === "tall"
                ? "row-span-2"
                : "";
            return (
              <button
                key={item.originalIndex}
                onClick={() => openLightbox(idx)}
                data-testid={`realisation-item-${item.originalIndex}`}
                className={`relative group overflow-hidden bg-[#111111] border border-white/5 hover:border-[#007AFF]/50 transition-all ${spanClasses}`}
              >
                {item.type === "video" ? (
                  <LazyVideo src={item.src} />
                ) : (
                  <img
                    src={item.src}
                    alt={item.title}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                )}

                {/* Dark overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/10 to-transparent opacity-80 group-hover:opacity-100 transition-opacity pointer-events-none" />

                {/* Type badge */}
                <div className="absolute top-3 left-3 flex items-center gap-1.5 px-2.5 py-1 bg-black/70 backdrop-blur-sm border border-white/20 text-[10px] uppercase tracking-widest font-semibold">
                  {item.type === "video" ? (
                    <>
                      <Play
                        className="w-3 h-3 text-[#EF4135]"
                        fill="currentColor"
                      />
                      <span>Vidéo</span>
                    </>
                  ) : (
                    <>
                      <ImageIcon className="w-3 h-3 text-[#007AFF]" />
                      <span>Photo</span>
                    </>
                  )}
                </div>

                {/* Title */}
                <div className="absolute inset-x-0 bottom-0 p-3 md:p-4 pointer-events-none">
                  <div className="text-[10px] uppercase tracking-[0.3em] text-white/50 mb-1">
                    / {String(item.originalIndex + 1).padStart(2, "0")}
                  </div>
                  <div className="font-heading text-xs md:text-sm font-bold uppercase tracking-tight text-white line-clamp-1">
                    {item.title}
                  </div>
                </div>

                {/* Hover accent line */}
                <div className="absolute bottom-0 left-0 h-[2px] w-0 bg-[#007AFF] group-hover:w-full transition-all duration-500" />
              </button>
            );
          })}
        </div>

        {/* Load more */}
        {hasMore && (
          <div className="mt-12 flex items-center justify-center">
            <button
              onClick={() => setVisibleCount((v) => v + 12)}
              data-testid="realisations-load-more"
              className="inline-flex items-center gap-3 border border-white/20 hover:border-[#007AFF] hover:text-[#007AFF] text-white text-xs font-bold uppercase tracking-widest px-8 py-4 transition-colors"
            >
              Voir plus
              <span className="text-white/40">
                ({filtered.length - visibleCount} restants)
              </span>
            </button>
          </div>
        )}
      </div>

      {/* Lightbox */}
      {lightbox !== null && filtered[lightbox] && (
        <div
          data-testid="realisations-lightbox"
          className="fixed inset-0 z-[70] bg-black/95 backdrop-blur-md flex items-center justify-center p-4 md:p-8 animate-fade-up"
          onClick={closeLightbox}
        >
          <button
            data-testid="lightbox-close"
            onClick={closeLightbox}
            className="absolute top-6 right-6 z-10 w-12 h-12 flex items-center justify-center border border-white/20 hover:bg-white hover:text-black transition-colors"
            aria-label="Fermer"
          >
            <X className="w-5 h-5" />
          </button>

          <button
            onClick={(e) => {
              e.stopPropagation();
              prev();
            }}
            className="absolute left-4 md:left-8 z-10 w-12 h-12 flex items-center justify-center border border-white/20 hover:bg-white hover:text-black transition-colors"
            aria-label="Précédent"
            data-testid="lightbox-prev"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>

          <button
            onClick={(e) => {
              e.stopPropagation();
              next();
            }}
            className="absolute right-4 md:right-8 z-10 w-12 h-12 flex items-center justify-center border border-white/20 hover:bg-white hover:text-black transition-colors"
            aria-label="Suivant"
            data-testid="lightbox-next"
          >
            <ChevronRight className="w-6 h-6" />
          </button>

          <div
            className="relative max-w-6xl max-h-[90vh] w-full"
            onClick={(e) => e.stopPropagation()}
          >
            {filtered[lightbox].type === "video" ? (
              <video
                key={filtered[lightbox].src}
                src={filtered[lightbox].src}
                controls
                autoPlay
                loop
                playsInline
                className="w-full max-h-[85vh] object-contain bg-black"
              />
            ) : (
              <img
                src={filtered[lightbox].src}
                alt={filtered[lightbox].title}
                className="w-full max-h-[85vh] object-contain"
              />
            )}
            <div className="mt-4 flex items-center justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-white/50">
                  {lightbox + 1} / {filtered.length}
                </div>
                <div className="font-heading text-lg font-bold uppercase tracking-tight mt-1">
                  {filtered[lightbox].title}
                </div>
              </div>
              <div className="flex items-center gap-1">
                <div className="h-[2px] w-8 bg-[#0055A4]" />
                <div className="h-[2px] w-8 bg-white" />
                <div className="h-[2px] w-8 bg-[#EF4135]" />
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};
